import { useState, useEffect } from 'react'
import { settingsAPI } from '../../services/api'

import { logger } from '@/utils/logger'

export default function SettingsManagement() {
  const [settings, setSettings] = useState([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(null)
  const [success, setSuccess] = useState('')
  const [error, setError] = useState('')
  const [showSmtpPassword, setShowSmtpPassword] = useState(false)

  useEffect(() => {
    loadSettings()
  }, [])

  const loadSettings = async () => {
    setLoading(true)
    try {
      const response = await settingsAPI.getAll()
      // Filter nur Nicht-Footer-Settings (E-Mail und andere)
      const filteredSettings = response.data.filter(s => !s.key.startsWith('footer_'))
      setSettings(filteredSettings)
    } catch (err) {
      setError('Fehler beim Laden der Einstellungen')
      logger.error('Error loading settings:', err)
    } finally {
      setLoading(false)
    }
  }

  const handleChange = (key, value) => {
    setSettings(settings.map(s =>
      s.key === key ? { ...s, value } : s
    ))
  }

  const handleSave = async (setting) => {
    setSaving(setting.key)
    setError('')
    setSuccess('')

    try {
      await settingsAPI.update(setting.key, setting.value)
      setSuccess(`${setting.description} wurde gespeichert`)
      setTimeout(() => setSuccess(''), 3000)
    } catch (err) {
      setError(`Fehler beim Speichern von ${setting.description}`)
      logger.error('Error saving setting:', err)
    } finally {
      setSaving(null)
    }
  }

  const handleSaveSmtp = async () => {
    setSaving('smtp')
    setError('')
    setSuccess('')

    try {
      const smtpSettings = settings.filter(s => s.key.startsWith('smtp_'))

      // Alle SMTP-Settings speichern
      await Promise.all(
        smtpSettings.map(setting => settingsAPI.update(setting.key, setting.value))
      )

      setSuccess('SMTP-Einstellungen wurden gespeichert')
      setTimeout(() => setSuccess(''), 3000)
    } catch (err) {
      setError('Fehler beim Speichern der SMTP-Einstellungen')
      logger.error('Error saving SMTP settings:', err)
    } finally {
      setSaving(null)
    }
  }

  const handleSaveEmail = async () => {
    setSaving('email')
    setError('')
    setSuccess('')

    try {
      const emailSettings = settings.filter(s => s.key.startsWith('email_'))

      // Alle E-Mail-Settings speichern
      await Promise.all(
        emailSettings.map(setting => settingsAPI.update(setting.key, setting.value))
      )

      setSuccess('E-Mail-Einstellungen wurden gespeichert')
      setTimeout(() => setSuccess(''), 3000)
    } catch (err) {
      setError('Fehler beim Speichern der E-Mail-Einstellungen')
      logger.error('Error saving email settings:', err)
    } finally {
      setSaving(null)
    }
  }

  const handleSaveContact = async () => {
    setSaving('contact')
    setError('')
    setSuccess('')
    try {
      await Promise.all(
        settings
          .filter(s => s.key.startsWith('contact_'))
          .map(s => settingsAPI.update(s.key, s.value))
      )
      setSuccess('Kontaktseite-Einstellungen wurden gespeichert')
      setTimeout(() => setSuccess(''), 3000)
    } catch {
      setError('Fehler beim Speichern der Kontaktseite-Einstellungen')
    } finally {
      setSaving(null)
    }
  }

  return (
    <div>
      <h1 style={{ fontSize: 24, margin: '0 0 24px' }}>Systemeinstellungen</h1>

      {error && <div className="wc-alert wc-alert-error" style={{ marginBottom: 16 }}>{error}</div>}
      {success && <div className="wc-alert wc-alert-success" style={{ marginBottom: 16 }}>{success}</div>}

      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '60px 0' }}>
          <div className="wc-spinner" />
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
          {/* E-Mail Einstellungen */}
          <div className="wc-card wc-elev-sm" style={{ gap: 16 }}>
            <div>
              <h3 className="wc-card-title" style={{ margin: 0 }}>E-Mail-Konfiguration</h3>
              <p className="wc-muted" style={{ fontSize: 13, margin: '4px 0 0' }}>
                Konfigurieren Sie hier den E-Mail-Versand für Benachrichtigungen, Newsletter und Passwort-Wiederherstellung.
              </p>
            </div>

            {settings.filter(s => s.key.startsWith('email_')).length > 0 ? (
              <>
                <div className="wc-field">
                  <label>Admin E-Mail-Adresse für Benachrichtigungen</label>
                  <input
                    type="email"
                    value={settings.find(s => s.key === 'email_admin_address')?.value || ''}
                    onChange={(e) => handleChange('email_admin_address', e.target.value)}
                    className="wc-input"
                    placeholder="admin@example.com"
                  />
                  <p className="wc-muted" style={{ fontSize: 11, margin: '4px 0 0' }}>
                    An diese Adresse werden Kontaktformular-Benachrichtigungen gesendet
                  </p>
                </div>

                <div className="wc-field">
                  <label>Absender-Name für ausgehende E-Mails</label>
                  <input
                    type="text"
                    value={settings.find(s => s.key === 'email_from_name')?.value || ''}
                    onChange={(e) => handleChange('email_from_name', e.target.value)}
                    className="wc-input"
                    placeholder="Werkstatt-Connect"
                  />
                  <p className="wc-muted" style={{ fontSize: 11, margin: '4px 0 0' }}>
                    Angezeigter Name bei ausgehenden E-Mails
                  </p>
                </div>

                <div className="wc-field">
                  <label>E-Mail-Benachrichtigungen für Kontaktanfragen</label>
                  <select
                    value={settings.find(s => s.key === 'email_notifications_enabled')?.value || 'true'}
                    onChange={(e) => handleChange('email_notifications_enabled', e.target.value)}
                    className="wc-input"
                  >
                    <option value="true">Aktiviert</option>
                    <option value="false">Deaktiviert</option>
                  </select>
                  <p className="wc-muted" style={{ fontSize: 11, margin: '4px 0 0' }}>
                    E-Mail-Benachrichtigungen bei neuen Kontaktanfragen ein-/ausschalten
                  </p>
                </div>

                <div className="wc-field">
                  <label>E-Mail-Benachrichtigungen für Support-Tickets</label>
                  <select
                    value={settings.find(s => s.key === 'email_support_notifications_enabled')?.value || 'true'}
                    onChange={(e) => handleChange('email_support_notifications_enabled', e.target.value)}
                    className="wc-input"
                  >
                    <option value="true">Aktiviert</option>
                    <option value="false">Deaktiviert</option>
                  </select>
                  <p className="wc-muted" style={{ fontSize: 11, margin: '4px 0 0' }}>
                    E-Mail-Benachrichtigungen bei neuen Support-Tickets ein-/ausschalten
                  </p>
                </div>

                <div className="wc-field">
                  <label>Support E-Mail-Adresse (optional)</label>
                  <input
                    type="email"
                    value={settings.find(s => s.key === 'email_support_address')?.value || ''}
                    onChange={(e) => handleChange('email_support_address', e.target.value)}
                    className="wc-input"
                    placeholder="support@example.com (leer lassen für Admin-Adresse)"
                  />
                  <p className="wc-muted" style={{ fontSize: 11, margin: '4px 0 0' }}>
                    Separate E-Mail-Adresse für Support-Benachrichtigungen. Wenn leer, wird die Admin-Adresse verwendet.
                  </p>
                </div>

                <button
                  onClick={handleSaveEmail}
                  disabled={saving === 'email'}
                  className="wc-btn wc-btn-primary wc-btn-block"
                >
                  {saving === 'email' ? 'Speichern...' : 'E-Mail-Einstellungen speichern'}
                </button>
              </>
            ) : (
              <div className="wc-alert wc-alert-info">
                Keine E-Mail-Einstellungen gefunden. Führen Sie das <code className="wc-mono">init_settings.py</code> Skript aus, um die E-Mail-Einstellungen zu initialisieren.
              </div>
            )}
          </div>

          {/* SMTP-Konfiguration */}
          <div className="wc-card wc-elev-sm" style={{ gap: 16 }}>
            <h3 className="wc-card-title" style={{ margin: 0 }}>SMTP-Server-Konfiguration</h3>

            {settings.filter(s => s.key.startsWith('smtp_')).length > 0 ? (
              <>
                <div className="wc-field">
                  <label>SMTP Server-Adresse</label>
                  <input
                    type="text"
                    value={settings.find(s => s.key === 'smtp_host')?.value || ''}
                    onChange={(e) => handleChange('smtp_host', e.target.value)}
                    className="wc-input"
                    placeholder="mail.example.com"
                  />
                  <p className="wc-muted" style={{ fontSize: 11, margin: '4px 0 0' }}>
                    Adresse des SMTP-Servers (z.B. smtp.gmail.com, mail.example.com)
                  </p>
                </div>

                <div className="wc-field">
                  <label>SMTP Port</label>
                  <select
                    value={settings.find(s => s.key === 'smtp_port')?.value || '587'}
                    onChange={(e) => handleChange('smtp_port', e.target.value)}
                    className="wc-input"
                  >
                    <option value="587">587 (STARTTLS)</option>
                    <option value="465">465 (SSL/TLS)</option>
                  </select>
                  <p className="wc-muted" style={{ fontSize: 11, margin: '4px 0 0' }}>
                    Port 587 für STARTTLS, Port 465 für SSL/TLS
                  </p>
                </div>

                <div className="wc-field">
                  <label>SMTP Benutzername</label>
                  <input
                    type="text"
                    value={settings.find(s => s.key === 'smtp_user')?.value || ''}
                    onChange={(e) => handleChange('smtp_user', e.target.value)}
                    className="wc-input"
                    placeholder="user@example.com"
                  />
                  <p className="wc-muted" style={{ fontSize: 11, margin: '4px 0 0' }}>
                    Benutzername für SMTP-Authentifizierung
                  </p>
                </div>

                <div className="wc-field">
                  <label>SMTP Passwort</label>
                  <div style={{ position: 'relative' }}>
                    <input
                      type={showSmtpPassword ? 'text' : 'password'}
                      value={settings.find(s => s.key === 'smtp_password')?.value || ''}
                      onChange={(e) => handleChange('smtp_password', e.target.value)}
                      className="wc-input"
                      placeholder="••••••••"
                      style={{ paddingRight: 36 }}
                    />
                    <button
                      type="button"
                      onClick={() => setShowSmtpPassword(!showSmtpPassword)}
                      className="wc-muted"
                      style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', background: 'transparent', border: 0, cursor: 'pointer', padding: 2 }}
                    >
                      {showSmtpPassword ? (
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                        </svg>
                      ) : (
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                        </svg>
                      )}
                    </button>
                  </div>
                  <p className="wc-muted" style={{ fontSize: 11, margin: '4px 0 0' }}>
                    Passwort für SMTP-Authentifizierung
                  </p>
                </div>

                <div className="wc-field">
                  <label>SMTP Absender-Adresse</label>
                  <input
                    type="email"
                    value={settings.find(s => s.key === 'smtp_from')?.value || ''}
                    onChange={(e) => handleChange('smtp_from', e.target.value)}
                    className="wc-input"
                    placeholder="noreply@example.com"
                  />
                  <p className="wc-muted" style={{ fontSize: 11, margin: '4px 0 0' }}>
                    Absender-Adresse für ausgehende E-Mails
                  </p>
                </div>

                <button
                  onClick={handleSaveSmtp}
                  disabled={saving === 'smtp'}
                  className="wc-btn wc-btn-primary wc-btn-block"
                >
                  {saving === 'smtp' ? 'Speichern...' : 'SMTP-Einstellungen speichern'}
                </button>
              </>
            ) : (
              <div className="wc-alert wc-alert-info">
                Keine SMTP-Einstellungen gefunden. Führen Sie das <code className="wc-mono">init_settings.py</code> Skript aus, um die SMTP-Einstellungen zu initialisieren.
              </div>
            )}
          </div>

          {/* Kontaktseite */}
          {settings.filter(s => s.key.startsWith('contact_')).length > 0 && (
            <div className="wc-card wc-elev-sm" style={{ gap: 16 }}>
              <div>
                <h3 className="wc-card-title" style={{ margin: 0 }}>Kontaktseite</h3>
                <p className="wc-muted" style={{ fontSize: 13, margin: '4px 0 0' }}>
                  Diese Angaben erscheinen unter dem Kontaktformular auf der öffentlichen Kontaktseite. Lassen Sie ein Feld leer, um es auszublenden.
                </p>
              </div>

              {[
                { key: 'contact_email', label: 'E-Mail-Adresse', placeholder: 'info@beispiel.de', type: 'email' },
                { key: 'contact_phone', label: 'Telefonnummer', placeholder: '+49 (0) 123 456789', type: 'text' },
                { key: 'contact_address', label: 'Adresse', placeholder: 'Musterstraße 1, 12345 Stadt', type: 'text' },
                { key: 'contact_hours_1', label: 'Öffnungszeiten Zeile 1', placeholder: 'Montag - Freitag: 9:00 - 18:00 Uhr', type: 'text' },
                { key: 'contact_hours_2', label: 'Öffnungszeiten Zeile 2', placeholder: 'Samstag: 10:00 - 14:00 Uhr', type: 'text' },
                { key: 'contact_hours_3', label: 'Öffnungszeiten Zeile 3', placeholder: 'Sonntag: Geschlossen', type: 'text' },
              ].map(({ key, label, placeholder, type }) => (
                <div key={key} className="wc-field">
                  <label>{label}</label>
                  <input
                    type={type}
                    value={settings.find(s => s.key === key)?.value || ''}
                    onChange={(e) => handleChange(key, e.target.value)}
                    className="wc-input"
                    placeholder={placeholder}
                  />
                </div>
              ))}

              <button
                onClick={handleSaveContact}
                disabled={saving === 'contact'}
                className="wc-btn wc-btn-primary wc-btn-block"
              >
                {saving === 'contact' ? 'Speichern...' : 'Kontaktseite speichern'}
              </button>
            </div>
          )}

          {/* Andere Settings falls vorhanden */}
          {settings.filter(s => !s.key.startsWith('email_') && !s.key.startsWith('smtp_') && !s.key.startsWith('contact_')).length > 0 && (
            <div className="wc-card wc-elev-sm" style={{ gap: 16 }}>
              <h3 className="wc-card-title" style={{ margin: 0 }}>Weitere Einstellungen</h3>
              {settings.filter(s => !s.key.startsWith('email_') && !s.key.startsWith('smtp_') && !s.key.startsWith('contact_')).map((setting) => (
                <div key={setting.key} className="wc-field">
                  <label>{setting.description}</label>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <input
                      type="text"
                      value={setting.value || ''}
                      onChange={(e) => handleChange(setting.key, e.target.value)}
                      className="wc-input"
                      style={{ flex: 1 }}
                    />
                    <button
                      onClick={() => handleSave(setting)}
                      disabled={saving === setting.key}
                      className="wc-btn wc-btn-primary"
                      style={{ whiteSpace: 'nowrap' }}
                    >
                      {saving === setting.key ? 'Speichern...' : 'Speichern'}
                    </button>
                  </div>
                  <p className="wc-muted" style={{ fontSize: 11, margin: '4px 0 0' }}>
                    Schlüssel: {setting.key} | Öffentlich: {setting.is_public ? 'Ja' : 'Nein'}
                  </p>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  )
}
